/* eslint-disable no-lonely-if */
/* eslint-disable no-nested-ternary */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';
import Center from '../../assets/styles/Center';
import FormContainer from '../../assets/styles/FormContainer';
import Text from '../../assets/styles/Text';
import Error from '../../components/Error';
import Loader from '../../components/Loader';
import Verification from '../../components/Verification';
import { useSaveModelComplete } from '../../hooks/mutations/useModelMutations';
import { useCities } from '../../hooks/queries/useCities';
import {
  useFiltersByGender,
  useModelFilters,
} from '../../hooks/queries/useFilters';
import { useModelServices, useServices } from '../../hooks/queries/useServices';
import { useModelById } from '../../hooks/queries/useUsers';
import useErrors from '../../hooks/useErrors';
import { useStore } from '../../stores';
import calculateAge from '../../utils/calculateAge';
import BasicInfoSection from './components/BasicInfoSection';
import FiltersSection from './components/FiltersSection';
import ServicesSection from './components/ServicesSection';
import * as styles from './styles';

export default function ModelRequired() {
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedServices, setSelectedServices] = useState({});
  const [formData, setFormData] = useState({
    sex: '',
    name: '',
    dateOfBirth: '',
    nameFantasy: '',
    impactPhrase: '',
    description: '',
    selectedCity: '',
    district: '',
  });

  const navigate = useNavigate();
  const { hash } = useLocation();

  const { setError, removeError, getErrorMessageByFieldName, errors } =
    useErrors();

  const { user, setModelVerification } = useStore(
    useShallow((state) => ({
      user: state.user,
      setModelVerification: state.setModelVerification,
    }))
  );

  const {
    data: userData,
    isLoading: isLoadingUser,
    isError: isErrorUser,
    error: userError,
  } = useModelById(user?.id);

  const {
    data: cities = [],
    isError: isErrorCities,
    error: citiesError,
  } = useCities();

  const {
    data: allServices = [],
    isError: isErrorServices,
    error: servicesError,
  } = useServices();

  const {
    data: modelServices = [],
    isError: isErrorModelServices,
    error: modelServicesError,
  } = useModelServices();

  const {
    data: modelFilters = [],
    isError: isErrorModelFilters,
    error: modelFiltersError,
  } = useModelFilters();

  const {
    data: filters,
    isLoading: isLoadingFilters,
    isError: isErrorFilters,
    error: filtersError,
  } = useFiltersByGender(formData.sex || 'WOMAN');

  const { saveAll, isLoading: isSaving } = useSaveModelComplete();

  // Load initial meta
  useEffect(() => {
    if (!userData) return;

    setFormData({
      sex: userData.gender || '',
      name: userData.name || '',
      nameFantasy: userData.fantasyName || '',
      impactPhrase: userData.impactPhrase || '',
      description: userData.description || '',
      selectedCity: userData.cityId || '',
      district: userData.district || '',
      dateOfBirth: userData.dateOfBirth || '',
    });
  }, [userData]);

  // Load initial model Filters
  useEffect(() => {
    if (modelFilters.length === 0) return;

    const filtersObj = {};
    modelFilters.forEach((f) => {
      filtersObj[f.filterId] = f.content;
    });
    setSelectedFilters(filtersObj);
  }, [modelFilters]);

  // Load initial model Services
  useEffect(() => {
    if (modelServices.length === 0) return;

    const servicesObj = {};
    modelServices.forEach((s) => {
      servicesObj[s.serviceId] = s.optionType;
    });
    setSelectedServices(servicesObj);
  }, [modelServices]);

  // Valid form
  const isFormValid = useMemo(() => {
    const { sex, name, dateOfBirth } = formData;
    return Boolean(sex && name && dateOfBirth) && errors.length === 0;
  }, [formData, errors]);

  // Handlers
  const updateFormField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFilterToggle = (filterId, value = null, isMultiple = false) => {
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };

      // Filtro múltiplo - trabalha com arrays
      if (isMultiple && value) {
        const currentValues = Array.isArray(newFilters[filterId])
          ? newFilters[filterId]
          : [];

        if (currentValues.includes(value)) {
          // Remove o valor se já existe
          const filtered = currentValues.filter((v) => v !== value);
          if (filtered.length === 0) {
            delete newFilters[filterId];
          } else {
            newFilters[filterId] = filtered;
          }
        } else {
          // Adiciona o novo valor
          newFilters[filterId] = [...currentValues, value];
        }
      }
      // Filtro com valor único (não múltiplo)
      else if (value) {
        if (newFilters[filterId] === value) {
          delete newFilters[filterId];
        } else {
          newFilters[filterId] = value;
        }
      }
      // Filtro booleano
      else {
        if (newFilters[filterId] === true) {
          delete newFilters[filterId];
        } else {
          newFilters[filterId] = true;
        }
      }

      return newFilters;
    });
  };
  const handleServiceChange = (serviceId, value) => {
    setSelectedServices((prev) => ({
      ...prev,
      [serviceId]: value,
    }));
  };

  const handleServiceRemove = (serviceId) => {
    setSelectedServices((prev) => {
      const newServices = { ...prev };
      delete newServices[serviceId];
      return newServices;
    });
  };

  // Validate age
  const validateAge = (dateOfBirth) => {
    const age = calculateAge(dateOfBirth);
    if (age < 18) {
      setError({
        field: 'date',
        message: 'É necessário ter pelo menos 18 anos',
      });
      return false;
    }
    return true;
  };

  // Send function
  const onSubmit = async () => {
    if (!isFormValid) {
      toast.error(
        'Preencha todos os campos obrigatórios da seção "Dados Básicos"'
      );
      return;
    }

    if (!validateAge(formData.dateOfBirth)) {
      return;
    }

    try {
      const basicData = {
        gender: formData.sex,
        name: formData.name,
        dateOfBirth: formData.dateOfBirth,
      };

      const descriptionData = {
        fantasyName: formData.nameFantasy,
        description: formData.description,
        impactPhrase: formData.impactPhrase,
        cityId: formData.selectedCity,
        district: formData.district,
      };

      const servicesToSave = Object.entries(selectedServices)
        .filter(([, optionType]) => optionType && optionType.trim() !== '')
        .map(([serviceId, optionType]) => ({ serviceId, optionType }));

      await saveAll({
        basicData,
        descriptionData,
        filters: selectedFilters,
        services: servicesToSave,
      });

      setModelVerification({ dateOfBirth: formData.dateOfBirth });
      toast.success('Dados salvos com sucesso!');

      const nextPage =
        hash === '#edit' ? '/modeluploads/#edit' : '/modeldashboard';
      navigate(nextPage);
    } catch (error) {
      toast.error('Erro ao salvar dados. Tente novamente.');
    }
  };

  // ✅ Verificar erros
  const isError =
    isErrorUser ||
    isErrorCities ||
    isErrorServices ||
    isErrorModelServices ||
    isErrorModelFilters ||
    isErrorFilters;

  const errorMessage =
    userError?.message ||
    citiesError?.message ||
    servicesError?.message ||
    modelServicesError?.message ||
    modelFiltersError?.message ||
    filtersError?.message;

  if (isLoadingUser) {
    return <Loader isLoading />;
  }

  if (isError) {
    return <Error isError message={errorMessage} />;
  }

  return (
    <>
      {isSaving && <Loader isLoading />}

      <FormContainer>
        <Center className="center">
          <Verification v={6} back={false} />

          <styles.Container>
            {/* Conteúdo das Seções */}

            <BasicInfoSection
              formData={formData}
              updateFormField={updateFormField}
              cities={cities}
              getErrorMessageByFieldName={getErrorMessageByFieldName}
              removeError={removeError}
              setError={setError}
            />

            <FiltersSection
              filters={filters}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
              isLoading={isLoadingFilters}
            />

            <ServicesSection
              services={allServices}
              selectedServices={selectedServices}
              onServiceChange={handleServiceChange}
              onServiceRemove={handleServiceRemove}
            />

            <styles.Button
              type="button"
              onClick={onSubmit}
              disabled={isSaving || !isFormValid}
            >
              <Text.P $upper>
                {isSaving ? 'Salvando...' : 'CONFIRMO MINHAS INFORMAÇÕES'}
              </Text.P>
            </styles.Button>
          </styles.Container>
        </Center>
      </FormContainer>
    </>
  );
}
